import * as angular from 'angular';
import {Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges} from 'ng-metadata/core';
import './accordion.component.scss';

/**
 * @ngdoc directive
 * @name mwAccordion
 * @module mw.components.accordion
 *
 * @param {string} [title] - The title to be displayed in the header.
 * @param {boolean} [is-open=false] - Control if the accordion is open.
 * @param {number} [max-height=254] - Set the max-height of the accordion content.
 * @param {expression} [on-open] - Angular expression to evaluate when the accordion is opened. $event is a boolean indicating opened state (true).
 * @param {expression} [on-close] - Angular expression to evaluate when the accordion is closed. $event is a boolean indicating closed state (false).
 * @param {expression} [on-toggle] - Angular expression to evaluate when the accordion is toggled opened or closed. $event is a boolean indicating opened(true)/closed(false) state.
 *
 * @description
 * `<md-accordion>` is a component used to display a simple collapsible panel controlled through a click action on a button located in the header of the accordion. Both the accordion header, and the accordion content allow transclusion of other elements into their respective views.  Elements transcluded into the header will appear after the title.
 *
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-accordion title="Accordion Header" is-open="$ctrl.isAccordionOpen" on-open="$ctrl.handleAccordionOpen" on-close="$ctrl.handleAccordionClose">
 *      <mw-accordion-header>
 *          <div>Header Content</div>
 *      </mw-accordion-header>
 *      <mw-accordion-content>
 *          <div>Accordion Content</div>
 *      </mw-accordion-content>
 *      <mw-accordion-footer>
 *          <div>Footer Content</div>
 *      </mw-accordion-footer>
 *   </mw-accordion>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-accordion',
    template: `
        <div class="mw-accordion" ng-class="{'mw-accordion--expanded': $ctrl.isOpen, 'mw-accordion--has-subheader': $ctrl.isSubHeaderFilled()}" layout="column">
            <div class="mw-accordion__header" ng-class="{'mw-accordion__header--has-subheader': $ctrl.isSubHeaderFilled()}" layout="row" layout-align="space-between center" ng-click="$ctrl.handleToggleClick($event)">
                <div class="mw-accordion__header-panel" flex layout="row">
                    <span class="mw-accordion__header-panel-title" ng-if="$ctrl.title.length">{{ ::$ctrl.title }}</span>
                    <span ng-transclude="headerSlot" flex></span>
                </div>
                <md-button class="mw-button md-icon-button md-button mw-accordion__header-trigger"  md-no-ink="true" aria-label="{{'Open' | translate}}">
                    <md-icon class="mw-accordion__header-trigger-icon" ng-class="{'mw-accordion__header-trigger-icon--expanded': $ctrl.isOpen, 'mw-accordion__header-trigger-icon--collapsed': !$ctrl.isOpen}" md-svg-icon="hardware:ic_keyboard_arrow_down_24px"></md-icon>
                </md-button>
            </div>
            <md-content class="mw-accordion__content" ng-style="$ctrl.contentStyle">
                <div ng-transclude="subheaderSlot"></div>
                <div ng-transclude="contentSlot"></div>
            </md-content>
            <div ng-if="$ctrl.isOpen" class="mw-accordion__footer" ng-transclude="footerSlot"></div>
        </div>
    `,
    legacy: {
        transclude: {
            'headerSlot': '?mwAccordionHeader',
            'subheaderSlot': '?mwAccordionSubheader',
            'contentSlot': '?mwAccordionContent',
            'footerSlot': '?mwAccordionFooter'
        }
    }
})
export class AccordionComponent implements OnChanges {
    @Input('@') title: string;
    @Input('<') maxHeight: number = 254;
    @Input('<') isOpen: boolean = false;
    @Input('<') hasSubheader: boolean = false;
    @Output() onOpen = new EventEmitter<boolean>();
    @Output() onClose = new EventEmitter<boolean>();
    @Output() onToggle = new EventEmitter<boolean>();

    private contentStyle: object = {
        'padding': '0',
        'max-height': this.maxHeight + 'px'
    };

    constructor(@Inject('$transclude') private $transclude: angular.ITranscludeFunction) {
        'ngInject';
    }

    ngOnChanges(changes: SimpleChanges) {
        // see https://hotell.gitbooks.io/ng-metadata/content/docs/api/core/interface.html#onchanges
        if (changes.isOpen && changes.isOpen.currentValue === true) {
            this.open();
        } else {
            this.close();
        }
    }

    handleToggleClick(event) {
        if ( event.target.className.indexOf('mw-accordion__header') > -1 ||
            event.target.parentNode.tagName.toLowerCase().indexOf('mw-icon') > -1 ||
            event.target.className.indexOf('mw-accordion__header-trigger') > -1 ||
            event.target.tagName.toLowerCase().indexOf('mw-accordion-header') > -1 ||
            (event.target.parentNode.tagName &&
                event.target.parentNode.tagName.toLowerCase().indexOf('mw-accordion-header') > -1 )
            ) {
            this.toggle();
        }
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        this.contentStyle['max-height'] = '';
        this.contentStyle['max-height'] = this.maxHeight + 'px';
        this.contentStyle['padding'] = '8px 0';

        if (this.isSubHeaderFilled()) {
            this.contentStyle['padding'] = '0 0 8px 0';
        }

        this.isOpen = true;

        this.onToggle.emit(this.isOpen);
        this.onOpen.emit(this.isOpen);
    }

    close() {
        this.contentStyle['max-height'] = '0';
        this.contentStyle['padding'] = '0';

        this.isOpen = false;

        this.onToggle.emit(this.isOpen);
        this.onClose.emit(this.isOpen);
    }

    isSubHeaderFilled() {
        return this.$transclude.isSlotFilled('subheaderSlot');
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/accordion/accordion.component.ts